import { type ReactNode, useState } from 'react'

import { downloadRowsCsv } from '@/components/ui/table/exportRows'
import type { Filter } from '@/stores'
import { activityListColumns } from '../columns'
import {
  ACTIVITY_GROUP_LABELS,
  activityViewFor,
  hiddenCopyCounts,
} from '../model/activityView'
import type {
  ActivityEntry,
  ActivityGroup,
  ActivityType,
  ActivityView,
} from '../model/types'
import { type ListView, PortfolioTable } from '../PortfolioTable'
import { ActivityTotalsStrip } from './ActivityTotalsStrip'
import {
  activityFilterOptions,
  activityFilterSchema,
  activityQueryOf,
  type TypeChoice,
} from './activityQuery'
import {
  type ActivityListRow,
  activityExport,
  activityListRows,
} from './activityRows'
import {
  accountLabelsOf,
  accountNamesOf,
  type DetailScope,
  notCountedWithin,
} from './detailScope'
import { TypeChips } from './TypeChips'
import { type GroupOption, TypeGroups } from './TypeGroups'

type GroupKey = ActivityGroup | 'all'

const GROUP_KEYS: GroupKey[] = [
  'all',
  'money_movements',
  'trades',
  'income',
  'fees',
  'other',
]

interface ActivityTabProps {
  scope: DetailScope
  activity: ActivityEntry[]
  hiddenCopies: ActivityEntry[]
  slug: string
  renderStrip: (onExport: () => void) => ReactNode
}

const groupOptions = (view: ActivityView): GroupOption<GroupKey>[] =>
  GROUP_KEYS.map((key) => ({
    key,
    label: ACTIVITY_GROUP_LABELS[key],
    count: view.groupCounts[key],
  }))

const toggled = (chosen: ActivityType[], type: ActivityType) => {
  if (chosen.length === 0) return [type]
  return chosen.includes(type)
    ? chosen.filter((candidate) => candidate !== type)
    : [...chosen, type]
}

// The one merged list: each account's securities rows and cash ledger rows, with type groups,
// house filters and a totals strip.
export const ActivityTab = ({
  scope,
  activity,
  hiddenCopies,
  slug,
  renderStrip,
}: ActivityTabProps) => {
  const [choice, setChoice] = useState<TypeChoice>({ group: 'all', types: [] })
  const accountNames = accountNamesOf(scope)
  const baseView = activityViewFor(activity, scope.baseQuery)
  const columns = activityListColumns({ accountLabels: accountLabelsOf(scope) })

  const view = (
    filters: Filter[],
    search: string,
  ): ListView<ActivityListRow> => {
    const query = activityQueryOf(scope.baseQuery, filters, search, choice)
    const result = activityViewFor(activity, query)
    return {
      rows: activityListRows(result, query),
      key: JSON.stringify(query),
      totals: (
        <ActivityTotalsStrip
          totals={result.totals}
          window={result.dateSpan}
          accountCount={accountNames ? scope.sections.length : null}
          hiddenCopies={hiddenCopyCounts(hiddenCopies, query)}
          notCounted={notCountedWithin(
            scope.notCounted,
            result.dateSpan,
            query.accountIds,
          )}
        />
      ),
      groups: (
        <TypeGroups
          options={groupOptions(result)}
          active={choice.group}
          onChange={(group) => setChoice({ group, types: [] })}
        />
      ),
      chips: (
        <TypeChips
          group={choice.group}
          typeCounts={result.typeCounts}
          chosen={choice.types}
          onToggle={(type) =>
            setChoice({
              group: choice.group,
              types: toggled(choice.types, type),
            })
          }
        />
      ),
    }
  }

  return (
    <PortfolioTable
      contextId={`project-${slug}-portfolio-activity-list`}
      dialogId="portfolio-activity-list"
      schema={activityFilterSchema(accountNames)}
      getUniqueValues={activityFilterOptions(accountNames, baseView.typeCounts)}
      columns={columns}
      emptyMessage="No activity matches these filters."
      view={view}
      renderStrip={(rows) =>
        renderStrip(() =>
          downloadRowsCsv(activityExport(rows), `${slug}-activity.csv`),
        )
      }
    />
  )
}
